import type { AIActivity, FileCitation } from '../types/chat.types';
import { parseSSEChunk } from './streamParsing';
import { getActivityFromEvent } from './activityMapping';
import {
  type ParsedEvent,
  isRecord,
  getStringFrom,
  getNumberFrom,
  getEventType,
  isOpenAIError,
  isOutputTextDeltaEvent,
  isOutputTextAnnotationAddedEvent,
  isOutputTextDoneEvent,
  isResponseCompletedEvent,
  isEndEvent,
  isReasoningSummaryTextDeltaEvent,
} from './streamProcessing.utils';

/**
 * Utilities for processing streaming responses
 */

interface EventProcessingContext {
  eventType: string;
  parsedEvent: ParsedEvent;
  eventData: string;
  hasSearched: boolean;
  onActivityChange?: (activity: AIActivity) => void;
  onCitation?: (citation: FileCitation) => void;
  onChunk: (chunk: string) => void;
  onReasoningSummary?: (delta: string) => void;
  seenCitationKeys: Set<string>;
}

/**
 * Log detailed error information internally while keeping user message generic
 */
const handleError = (
  errorDetails: unknown,
  userFacingMessage: string,
): Error => {
  // eslint-disable-next-line no-console
  console.error('[Stream Processing Error]', {
    timestamp: new Date().toISOString(),
    details: errorDetails,
  });

  return new Error(userFacingMessage);
};

/**
 * Process different event types with dedicated handlers
 */
function extractCitationsDeep(value: unknown): FileCitation[] {
  const citations: FileCitation[] = [];

  const visit = (node: unknown) => {
    if (!node) return;

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (!isRecord(node)) return;

    const type = getStringFrom(node, 'type');
    const fileId = getStringFrom(node, 'file_id');
    const filename = getStringFrom(node, 'filename');
    const index = getNumberFrom(node, 'index');

    if (type === 'file_citation' && fileId && filename) {
      citations.push({
        type: 'file_citation',
        file_id: fileId,
        filename,
        index: index ?? 0,
      });
    }

    Object.values(node).forEach(visit);
  };

  visit(value);

  return citations;
}

/**
 * Process different event types with dedicated handlers
 */
const processEventByType = (context: EventProcessingContext): boolean => {
  const {
    eventType,
    parsedEvent,
    eventData,
    hasSearched,
    onActivityChange,
    onCitation,
    onChunk,
    onReasoningSummary,
    seenCitationKeys,
  } = context;

  const emitCitationIfNew = (citation: FileCitation) => {
    const key = `${citation.file_id}:${citation.index ?? ''}:${
      citation.filename ?? ''
    }`;
    if (seenCitationKeys.has(key)) return;
    seenCitationKeys.add(key);
    onCitation?.(citation);
  };

  // Track retrieval/search tool usage - return true if search was detected
  let searchDetected = false;
  if (
    eventType === 'response.file_search_call.searching' ||
    eventType === 'response.web_search_call.searching' ||
    eventType === 'response.web_search_call.in_progress'
  ) {
    searchDetected = true;
  }

  // Handle activity updates
  if (onActivityChange) {
    const activity = getActivityFromEvent(eventType, hasSearched, eventData);
    onActivityChange(activity);
  }

  if (isOutputTextAnnotationAddedEvent(parsedEvent)) {
    emitCitationIfNew({
      type: 'file_citation',
      file_id: parsedEvent.annotation.file_id,
      filename: parsedEvent.annotation.filename,
      index:
        typeof parsedEvent.annotation.index === 'number'
          ? parsedEvent.annotation.index
          : 0,
      annotation_index:
        typeof parsedEvent.annotation_index === 'number'
          ? parsedEvent.annotation_index
          : undefined,
    });
  }

  if (isOutputTextDoneEvent(parsedEvent)) {
    const doneCitations = extractCitationsDeep(parsedEvent);
    doneCitations.forEach(emitCitationIfNew);
  }

  // Handle reasoning summary streaming
  if (onReasoningSummary && isReasoningSummaryTextDeltaEvent(parsedEvent)) {
    onReasoningSummary(parsedEvent.delta);
  }

  // Handle content streaming
  if (isOutputTextDeltaEvent(parsedEvent)) {
    onChunk(parsedEvent.delta);
  }

  return searchDetected;
};

/**
 * Processes a streaming response from the server
 * @param response - The Response object containing the stream
 * @param onChunk - Callback function to handle each text chunk
 * @param onActivityChange - Optional callback to handle activity state changes
 * @param onCitation - Optional callback to handle file citations
 */
export const processStream = async (
  response: Response,
  onChunk: (chunk: string) => void,
  onActivityChange?: (activity: AIActivity) => void,
  onCitation?: (citation: FileCitation) => void,
  onReasoningSummary?: (delta: string) => void,
  onConversationId?: (conversationId: string) => void,
): Promise<string | null> => {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to get response stream reader');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let hasSearched = false; // Track if file search has been used
  let completedResponseId: string | null = null;
  const seenCitationKeys = new Set<string>();

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Process complete lines from buffer
      const lines = buffer.split('\n');

      // Keep the last partial line in buffer
      buffer = lines.pop() || '';

      // Parse complete lines
      const events = parseSSEChunk(lines.join('\n'));

      for (const event of events) {
        // Parse the event data as JSON to get the actual event type
        let parsedEvent: ParsedEvent | null = null;
        let eventType: string | undefined;

        try {
          if (event.data) {
            const parsed = JSON.parse(event.data) as unknown;
            if (!isRecord(parsed)) {
              continue;
            }
            parsedEvent = parsed;
            eventType = getEventType(parsedEvent);
          }
        } catch {
          // Skip events that aren't valid JSON
          continue;
        }

        if (!eventType || !event.data) {
          continue;
        }
        if (!parsedEvent) {
          continue;
        }

        if (eventType === 'conversation.created') {
          const conversationId = getStringFrom(parsedEvent, 'conversationId');
          if (conversationId) {
            onConversationId?.(conversationId);
          }
          continue;
        }

        // Handle OpenAI errors with proper logging and generic user message
        if (isOpenAIError(parsedEvent)) {
          throw handleError(
            {
              errorCode: parsedEvent.code,
              errorMessage: parsedEvent.message,
              errorParam: parsedEvent.param,
              sequenceNumber: parsedEvent.sequence_number,
            },
            'An error occurred while processing your request. Please try again.',
          );
        }

        // Handle generic errors
        if (eventType === 'error') {
          throw handleError(
            {
              eventType,
              message: parsedEvent.message || 'Unknown error',
            },
            'An error occurred while processing your request. Please try again.',
          );
        }

        // Process the event based on its type
        const searchDetected = processEventByType({
          eventType,
          parsedEvent,
          eventData: event.data,
          hasSearched,
          onActivityChange,
          onCitation,
          onChunk,
          onReasoningSummary,
          seenCitationKeys,
        });

        // Update hasSearched if search was detected
        if (searchDetected) {
          hasSearched = true;
        }

        // Handle completion and early returns
        if (isResponseCompletedEvent(parsedEvent)) {
          const completionPayload = (parsedEvent as { response?: unknown })
            .response;
          const payload = completionPayload ?? parsedEvent;
          const responseId = getStringFrom(completionPayload, 'id');

          if (responseId) {
            completedResponseId = responseId;
          }

          const completionCitations = extractCitationsDeep(payload);
          completionCitations.forEach(citation => {
            const key = `${citation.file_id}:${citation.index ?? ''}:${
              citation.filename ?? ''
            }`;
            if (seenCitationKeys.has(key)) return;
            seenCitationKeys.add(key);
            onCitation?.(citation);
          });
        }

        if (isResponseCompletedEvent(parsedEvent) || isEndEvent(parsedEvent)) {
          if (onActivityChange) {
            onActivityChange('completed');
          }
          return completedResponseId;
        }
      }
    }

    return completedResponseId;
  } finally {
    // Clean up reader
    reader.releaseLock();
  }
};
